
import { A2AMessage } from '@/utils/a2a/types';
import { createTaskRequest, createTaskResponse } from '@/utils/a2a/messaging/taskManagement';
import { broadcastNotification } from '@/utils/a2a/messaging/notifications';
import { publishMessage } from '@/utils/a2a/communication/messageBus';
import { createOrchestrationPlan } from './planCreation';
import { toast } from 'sonner';

export async function executeMultiAgentTask(
  task: string,
  capabilities: string[],
  complexity: 'simple' | 'medium' | 'complex',
  sendMessage: any,
  options: {
    channelId?: string;
    updatePlans?: () => void;
  } = {}
) {
  const plan = createOrchestrationPlan(task, capabilities, complexity);
  const channelId = options.channelId || `task-${Date.now()}`;
  
  try {
    broadcastNotification(
      'executive-agent',
      `Task initiated: ${task}. Complexity: ${complexity}. Required capabilities: ${capabilities.join(', ')}`,
      'info',
      plan.agentSequence
    );

    const pattern = plan.metadata?.pattern || 'sequential';
    const messages = pattern === 'parallel' 
      ? await executeParallelTask(plan, task, capabilities, channelId, sendMessage)
      : await executeSequentialTask(plan, task, channelId, sendMessage);

    options.updatePlans?.();
    return messages;
  } catch (error) {
    console.error("Error in orchestration:", error);
    toast.error("Failed to process the request");
    throw error;
  }
}

// Implementation of the missing functions
async function executeSequentialTask(plan: any, task: string, channelId: string, sendMessage: any): Promise<A2AMessage[]> {
  const messages: A2AMessage[] = [];
  let currentTask = task;

  // Process each agent in sequence
  for (let i = 0; i < plan.agentSequence.length; i++) {
    const agentId = plan.agentSequence[i];
    const isFirstAgent = i === 0;
    const isLastAgent = i === plan.agentSequence.length - 1;

    // Create appropriate task description for this agent
    const taskDescription = isFirstAgent 
      ? task
      : `Continue processing this task: ${currentTask}`;

    // Send message to agent
    const message = await sendMessage({
      fromAgent: isFirstAgent ? 'executive-agent' : plan.agentSequence[i-1],
      toAgent: agentId,
      content: taskDescription,
      signature: '',
      keyId: '',
      metadata: {
        protocol: 'A2A',
        version: '1.1',
        messageType: 'REQUEST',
        conversationId: channelId,
        sequentialStep: i + 1,
        totalSteps: plan.agentSequence.length,
        intent: 'sequential-task',
        priority: plan.complexity === 'complex' ? 'high' : 'normal'
      }
    });

    messages.push(message);
    
    // In a real implementation, we would wait for the agent's response
    // and use that as the currentTask for the next agent
    currentTask = `Agent ${agentId} processed: ${taskDescription.substring(0, 50)}...`;
  }

  return messages;
}

async function executeParallelTask(
  plan: any, 
  task: string, 
  capabilities: string[], 
  channelId: string, 
  sendMessage: any
): Promise<A2AMessage[]> {
  const messagePromises = plan.agentSequence.map(async (agentId: string) => {
    // Find relevant capabilities for this agent
    const agentCapabilities = capabilities;
    
    // Create task message
    return sendMessage({
      fromAgent: 'executive-agent',
      toAgent: agentId,
      content: `Process this task focusing on ${agentCapabilities.join(', ')}: ${task}`,
      signature: '',
      keyId: '',
      metadata: {
        protocol: 'A2A',
        version: '1.1',
        messageType: 'REQUEST',
        conversationId: channelId,
        parallelExecution: true,
        intent: 'parallel-task',
        priority: plan.complexity === 'complex' ? 'high' : 'normal'
      }
    });
  });
  
  // Wait for all messages to be sent
  const messages = await Promise.all(messagePromises);
  return messages;
}
